import firebase from "firebase"
import { db } from "../../../../../utils/firebase"
import { logActivity } from "../../../../enquiry/_components/activity/log"

/**
 * Status enum for an individual confirmation entry.
 * CONFIRMED  — supplier has confirmed the booking (green badge).
 * NOT_CONFIRMED — supplier reported the booking is not in place / needs action (amber badge).
 */
export const CONFIRMATION_STATUS = {
	CONFIRMED: "CONFIRMED",
	NOT_CONFIRMED: "NOT_CONFIRMED",
}

/**
 * Item kinds tracked in the confirmations tab. Hotels and rounds are
 * subcollections under each item; transfers and flight are single objects on
 * the booking doc, so their confirmations live in dedicated top-level
 * subcollections under the booking.
 */
export const ITEM_KIND = {
	HOTEL: "HOTEL",
	ROUND: "ROUND",
	TRANSFER: "TRANSFER",
	FLIGHT: "FLIGHT",
}

const KIND_LABELS = {
	[ITEM_KIND.HOTEL]: "Hotel",
	[ITEM_KIND.ROUND]: "Round",
	[ITEM_KIND.TRANSFER]: "Transfer",
	[ITEM_KIND.FLIGHT]: "Flight",
}

/**
 * Returns the Firestore collection path for a given item's confirmations.
 * For hotels/rounds the path is nested under the item; for transfer and flight
 * the path lives directly under the booking (since those are single-object
 * fields on the booking doc, not subcollections themselves).
 */
export function confirmationCollectionPath({ bookingID, kind, itemID }) {
	switch (kind) {
		case ITEM_KIND.HOTEL:
			return `bookings/${bookingID}/hotels/${itemID}/confirmations`
		case ITEM_KIND.ROUND:
			return `bookings/${bookingID}/rounds/${itemID}/confirmations`
		case ITEM_KIND.TRANSFER:
			return `bookings/${bookingID}/transfer_confirmations`
		case ITEM_KIND.FLIGHT:
			return `bookings/${bookingID}/flight_confirmations`
		default:
			throw new Error(`Unknown confirmation item kind: ${kind}`)
	}
}

const buildActivityLine = ({ kind, itemLabel, status, confirmedBy, reference }) => {
	const kindLabel = KIND_LABELS[kind] || "Item"
	const named = itemLabel ? `${kindLabel} (${itemLabel})` : kindLabel
	const verb = status === CONFIRMATION_STATUS.NOT_CONFIRMED ? "reported NOT confirmed" : "confirmed"
	const by = confirmedBy ? ` by ${confirmedBy}` : ""
	const ref = reference ? ` (Ref: ${reference})` : ""
	return `${named} ${verb}${by}${ref}`
}

/**
 * Add a new confirmation entry under an item and append a structured line to
 * the booking-level activity feed so the audit trail is visible alongside
 * other comms.
 *
 * `entry` shape: { status, reference, notes, receivedDate (Date | null), confirmedBy }
 */
export async function addConfirmation({ bookingID, kind, itemID, itemLabel, entry, userID }) {
	const collectionPath = confirmationCollectionPath({ bookingID, kind, itemID })
	const docRef = await db.collection(collectionPath).add({
		status: entry.status,
		reference: entry.reference || "",
		notes: entry.notes || "",
		received_date: entry.receivedDate
			? firebase.firestore.Timestamp.fromDate(new Date(entry.receivedDate))
			: firebase.firestore.FieldValue.serverTimestamp(),
		confirmed_by: entry.confirmedBy || "",
		item_id: itemID || null,
		item_kind: kind,
		user: userID || null,
		created: firebase.firestore.FieldValue.serverTimestamp(),
	})

	await logActivity(`bookings/${bookingID}`, "CONFIRMATION_ADDED", buildActivityLine({ kind, itemLabel, ...entry }), {
		confirmation_id: docRef.id,
		confirmation_path: collectionPath,
		item_kind: kind,
		item_id: itemID || null,
	})

	return docRef.id
}

/**
 * Update an existing confirmation entry. The pre-update label is captured by
 * the caller (we don't refetch) so the activity line reads naturally even if
 * the item label changes later.
 */
export async function updateConfirmation({ bookingID, kind, itemID, itemLabel, confirmationID, entry, userID }) {
	const collectionPath = confirmationCollectionPath({ bookingID, kind, itemID })
	const docPath = `${collectionPath}/${confirmationID}`

	await db.doc(docPath).set(
		{
			status: entry.status,
			reference: entry.reference || "",
			notes: entry.notes || "",
			received_date: entry.receivedDate
				? firebase.firestore.Timestamp.fromDate(new Date(entry.receivedDate))
				: firebase.firestore.FieldValue.serverTimestamp(),
			confirmed_by: entry.confirmedBy || "",
			updated: firebase.firestore.FieldValue.serverTimestamp(),
			updated_by: userID || null,
		},
		{ merge: true }
	)

	await logActivity(
		`bookings/${bookingID}`,
		"CONFIRMATION_UPDATED",
		`${buildActivityLine({ kind, itemLabel, ...entry })} — entry updated`,
		{
			confirmation_id: confirmationID,
			confirmation_path: collectionPath,
			item_kind: kind,
			item_id: itemID || null,
		}
	)
}

/**
 * Delete a confirmation entry. Hard delete — the activity-feed line keeps the
 * audit trail (it includes the ref + supplier contact at time of deletion).
 */
export async function deleteConfirmation({ bookingID, kind, itemID, itemLabel, confirmationID, snapshot, userID }) {
	const collectionPath = confirmationCollectionPath({ bookingID, kind, itemID })
	const docPath = `${collectionPath}/${confirmationID}`

	await db.doc(docPath).delete()

	const kindLabel = KIND_LABELS[kind] || "Item"
	const named = itemLabel ? `${kindLabel} (${itemLabel})` : kindLabel
	const ref = snapshot?.reference ? ` (ref ${snapshot.reference})` : ""
	const by = snapshot?.confirmed_by ? `, originally confirmed by ${snapshot.confirmed_by}` : ""

	await logActivity(
		`bookings/${bookingID}`,
		"CONFIRMATION_DELETED",
		`Confirmation for ${named} deleted${ref}${by}`,
		{
			confirmation_id: confirmationID,
			confirmation_path: collectionPath,
			item_kind: kind,
			item_id: itemID || null,
			deleted_by: userID || null,
		}
	)
}
